import React from "react";
import PropTypes from "prop-types";
import { Link, useLocation } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";
import {
  Box,
  Button,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Popover,
  Typography,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import ArrowForwardRounded from "@mui/icons-material/ArrowForwardRounded";

import SteamSignInButton from "../../auth/SteamSignInButton";
import PlaySafeSignInButton from "../../auth/PlaySafeSignInButton";
import { useTemplateTrack } from "../TemplateTrackingProvider";
import { colors } from "./colors";
import { motion } from "framer-motion";
import { PageContainer } from "./PageContainer";
import { ULTRA_WIDE_MEDIA } from "./breakpoints";
import { OVERFLOW_X_CLIP_SX } from "./layoutGuards";

const HEADER_CONTENT_MAX_WIDTH = "1800px";
const HEADER_INLINE_PADDING = "clamp(1rem, 0.65rem + 1.5vw, 3rem)";

const MotionBox = motion(Box);
const MotionButton = motion(Button);

const DARK_HERO_NAV_LINKS = [
  { label: "How it works", to: "/how-it-works" },
  { label: "Match Night", to: "/match-night" },
  { label: "Anti-Cheat", to: "/anti-cheat" },
  { label: "Reputation", to: "/reputation" },
  { label: "Trust & Safety", to: "/trust-and-safety", hideBelow: 1000 },
  { label: "Pricing", to: "/pricing" },
];

const LOGO_URL =
  "https://ouro-public-assets.s3.eu-west-2.amazonaws.com/init-logo-darkmode.png";

/** Inline nav + login; hamburger below this width. */
const NAV_INLINE_MIN_PX = 800;
const NAV_INLINE_MEDIA = `(min-width: ${NAV_INLINE_MIN_PX}px)`;

/** Above fixed header (1400) and mobile drawer paper (1500). */
const LOGIN_MENU_Z_INDEX = 1600;

const MOBILE_NAV_ITEM_SX = {
  borderRadius: "4px",
  py: 1.25,
  pl: 0,
  pr: 1,
  "&:hover": {
    backgroundColor: "transparent",
  },
  "&:hover .MuiListItemText-primary": {
    color: colors.text.primary,
  },
};

const DARK_HERO_PAGE_BG = "#080808";

const HEADER_SCROLL_TRANSITION = {
  duration: 0.45,
  ease: [0.22, 1, 0.36, 1],
};

const HEADER_SCROLLED_SHELL_SX = {
  background: `
    linear-gradient(
      180deg,
      rgba(12,14,16,0.72),
      rgba(8,10,12,0.58)
    )
  `,
  backdropFilter: "blur(20px)",
  WebkitBackdropFilter: "blur(20px)",
  border: "1px solid rgba(255,255,255,0.05)",
  boxShadow: `
    inset 0 1px 0 rgba(255,255,255,0.04),
    0 10px 40px rgba(0,0,0,0.28)
  `,
};

function useHeaderScrolled() {
  const [isScrolled, setIsScrolled] = React.useState(false);

  React.useEffect(() => {
    let rafId = 0;

    const update = () => {
      rafId = 0;
      setIsScrolled(window.scrollY > 0);
    };

    const onScroll = () => {
      if (!rafId) {
        rafId = window.requestAnimationFrame(update);
      }
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (rafId) {
        window.cancelAnimationFrame(rafId);
      }
    };
  }, []);

  return isScrolled;
}

const NAV_LINK_SX = {
  position: "relative",
  color: colors.text.primary,
  textTransform: "uppercase",
  fontWeight: 700,
  fontSize: "clamp(0.7rem, 0.35rem + 0.3vw, 1.15rem)",
  letterSpacing: { md: "0.1em", lg: "0.12em", xl: "0.14em" },
  fontFamily: "'Inter', system-ui, sans-serif",
  [`@media ${ULTRA_WIDE_MEDIA}`]: {
    fontSize: "0.95rem",
    letterSpacing: "0.14em",
    px: 2.5,
  },
  minWidth: "auto",
  whiteSpace: "nowrap",
  px: { md: 1.35, lg: 1.65, xl: 1.85 },
  py: 1.15,
  borderRadius: "6px",
  overflow: "hidden",
  transition: `
    color 220ms cubic-bezier(0.22,1,0.36,1),
    transform 220ms cubic-bezier(0.22,1,0.36,1),
    background-color 220ms cubic-bezier(0.22,1,0.36,1)
  `,
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    borderRadius: "inherit",
    opacity: 0,
    background: `
      linear-gradient(
        180deg,
        rgba(255,255,255,0.06),
        rgba(255,255,255,0.02)
      )
    `,
    transition: "opacity 220ms ease",
  },
  "&::after": {
    content: '""',
    position: "absolute",
    left: "50%",
    bottom: 0,
    width: "58%",
    height: "1px",
    background: colors.primary.main,
    boxShadow: `
      0 0 10px rgba(246,185,76,0.45),
      0 0 18px rgba(246,185,76,0.22)
    `,
    transform: "translateX(-50%) translateY(8px) ",
    opacity: 0,
    transition: `
      opacity 260ms cubic-bezier(0.22,1,0.36,1),
      transform 320ms cubic-bezier(0.22,1,0.36,1)
    `,
  },
  "&:hover": {
    color: colors.text.primary,
    transform: "translateY(-1px)",
    backgroundColor: "transparent",
    "&::before": {
      opacity: 1,
    },
    "&::after": {
      opacity: 1,
      transform: "translateX(-50%) translateY(0px)",
    },
  },
};

const LOGIN_BUTTON_SX = {
  position: "relative",
  px: "1.75rem",
  height: 44,
  borderRadius: "16px",
  border: "1px solid rgba(255,255,255,0.12)",
  background: "rgba(255,255,255,0.02)",
  color: colors.text.primary,
  fontSize: "0.8rem",
  fontWeight: 700,
  letterSpacing: "0.16em",
  textTransform: "uppercase",
  overflow: "hidden",
  transition: `
    border-color .25s ease,
    background .25s ease,
    color .25s ease,
    transform .25s ease
  `,
  [`@media ${ULTRA_WIDE_MEDIA}`]: {
    height: 56,
    minHeight: 56,
    px: "2.75rem",
    fontSize: "0.95rem",
    letterSpacing: "0.18em",
    borderRadius: "18px",
  },
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    background: `
      linear-gradient(
        180deg,
        rgba(232,163,61,0.95),
        rgba(200,135,42,0.95)
      )
    `,
    opacity: 0,
    transition: "opacity .25s ease",
    zIndex: 0,
  },
  "& span": {
    position: "relative",
    zIndex: 1,
  },
  "&:hover": {
    borderColor: "rgba(232,163,61,0.55)",
    color: "#090909",
    transform: "translateY(-1px)",
    "&::before": {
      opacity: 1,
    },
  },
  "&:focus-visible": {
    outline: "none",
    boxShadow: `
      0 0 0 3px rgba(232,163,61,0.15)
    `,
  },
};

const HEADER_BUTTON_ULTRA_SX = {
  [`@media ${ULTRA_WIDE_MEDIA}`]: {
    height: "3.25rem",
    minHeight: "3.25rem",
    fontSize: "1rem",
    borderRadius: "14px",
    "& .MuiButton-startIcon": {
      marginLeft: "12px",
    },
    "& .MuiButton-startIcon svg": {
      fontSize: "1.2rem",
    },
    "& svg": {
      maxHeight: "2.25rem",
      height: "2.25rem",
      width: "auto",
    },
  },
};

const LOGIN_MENU_PAPER_SX = {
  mt: 1.5,
  width: 320,
  px: 2.25,
  pt: 2.25,
  pb: 2.75,
  borderRadius: "14px",
  backgroundColor: "rgba(8,8,10,0.96)",
  border: "1px solid rgba(255,255,255,0.08)",
  backdropFilter: "blur(20px)",
  WebkitBackdropFilter: "blur(20px)",
  boxShadow: "0 24px 64px rgba(0,0,0,0.65)",
  overflow: "visible",
  [`@media ${ULTRA_WIDE_MEDIA}`]: {
    width: 360,
    px: 2.75,
    pt: 2.75,
    pb: 3,
    borderRadius: "16px",
    mt: 2,
  },
};

const LOGIN_MENU_TITLE_SX = {
  mb: 1.5,
  fontSize: "0.72rem",
  fontWeight: 600,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  color: "rgba(255,255,255,0.5)",
  [`@media ${ULTRA_WIDE_MEDIA}`]: {
    mb: 2,
    fontSize: "0.8rem",
    letterSpacing: "0.16em",
  },
};

const LOGIN_MENU_STACK_SX = {
  display: "flex",
  flexDirection: "column",
  gap: 1.25,
  width: "100%",
  [`@media ${ULTRA_WIDE_MEDIA}`]: {
    gap: 1.5,
  },
};

const LOGIN_MENU_ASSURANCE_SX = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  mt: "0.35rem",
  color: colors.text.muted,
};

const LOGIN_MENU_ASSURANCE_TEXT_SX = {
  fontSize: "0.75rem",
  color: colors.text.muted,
  lineHeight: 1.4,
  textAlign: "center",
  [`@media ${ULTRA_WIDE_MEDIA}`]: {
    fontSize: "0.9rem",
  },
};

const LOGIN_MENU_STEAM_SX = {
  position: "relative",
  overflow: "hidden",
  width: "100%",
  height: 44,
  minHeight: 44,
  px: "0.9rem",
  borderRadius: "12px",
  border: "1px solid rgba(255,210,120,0.24)",
  background: `
    linear-gradient(
      180deg,
      #f7cb79 0%,
      #f0b458 42%,
      #de8f2c 100%
    )
  `,
  color: "#120c04",
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.875rem",
  letterSpacing: "0.02em",
  fontFamily: "'Inter', system-ui, sans-serif",
  backdropFilter: "blur(10px)",
  boxShadow: `
    inset 0 1px 0 rgba(255,255,255,0.34),
    inset 0 -6px 12px rgba(120,60,0,0.18),
    0 10px 30px rgba(232,163,61,0.24),
    0 0 0 1px rgba(255,190,90,0.05)
  `,
  transition: `
    transform 260ms cubic-bezier(0.22,1,0.36,1),
    box-shadow 260ms cubic-bezier(0.22,1,0.36,1),
    filter 260ms cubic-bezier(0.22,1,0.36,1),
    border-color 260ms ease
  `,
  "& .MuiButton-startIcon": {
    marginRight: 0,
    marginLeft: 0,
    position: "relative",
    zIndex: 2,
  },
  "& .MuiButton-startIcon svg path": {
    fill: "currentColor",
  },
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: "-140%",
    width: "58%",
    height: "100%",
    background: `
      linear-gradient(
        90deg,
        transparent,
        rgba(255,255,255,0.38),
        transparent
      )
    `,
    transform: "skewX(-24deg)",
    transition: "left 900ms cubic-bezier(0.22,1,0.36,1)",
    pointerEvents: "none",
  },
  "&::after": {
    content: '""',
    position: "absolute",
    inset: 0,
    borderRadius: "inherit",
    background: `
      radial-gradient(
        circle at top,
        rgba(255,255,255,0.22),
        transparent 72%
      )
    `,
    opacity: 0.7,
    pointerEvents: "none",
  },
  "&:hover": {
    transform: "translateY(-2px)",
    borderColor: "rgba(255,220,150,0.4)",
    boxShadow: `
      inset 0 1px 0 rgba(255,255,255,0.42),
      inset 0 -10px 18px rgba(120,60,0,0.28),
      0 18px 42px rgba(232,163,61,0.34),
      0 0 26px rgba(246,185,76,0.16)
    `,
    filter: "brightness(1.04) saturate(1.04)",
    "&::before": {
      left: "155%",
    },
  },
  "&:active": {
    transform: "scale(0.985)",
  },
  ...HEADER_BUTTON_ULTRA_SX,
};

const LOGIN_MENU_GOOGLE_WRAP_SX = {
  display: "flex",
  justifyContent: "center",
  width: "100%",
  overflow: "visible",
  colorScheme: "light",
};

const LOGIN_MENU_PLAYSAFE_SX = {
  position: "relative",
  width: "100%",
  height: 44,
  minHeight: 44,
  px: "1.15rem",
  borderRadius: "12px",
  overflow: "hidden",
  border: "1px solid rgba(255,255,255,0.28)",
  color: "rgba(255,255,255,0.92)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: `
    transform 260ms cubic-bezier(0.22,1,0.36,1),
    border-color 260ms ease,
    background-color 260ms ease
  `,
  "&::after": {
    content: '""',
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    height: "0px",
    background: `
      linear-gradient(
        90deg,
        rgba(0,204,221,0),
        rgba(0,204,221,0.9),
        rgba(0,204,221,0)
      )
    `,
    transition: "height .28s ease",
  },
  "& svg": {
    maxWidth: "100%",
    height: "auto",
    maxHeight: "1.5rem",
  },
  "&:hover": {
    transform: "translateY(-2px)",
    backgroundColor: "#09090b",
    borderColor: "transparent",
    boxShadow: `
      inset 0 -1px 0 rgba(0,204,221,.15),
      0 8px 24px rgba(0,204,221,.08)
    `,
    "&::after": {
      height: "2px",
    },
  },
  "&:active": {
    transform: "scale(0.985)",
  },
  ...HEADER_BUTTON_ULTRA_SX,
};

const PLAY_NOW_BUTTON_SX = {
  position: "relative",
  overflow: "hidden",
  height: 44,
  minHeight: 44,
  px: { xs: 2.2, md: 2.8 },
  borderRadius: "14px",
  border: "1px solid rgba(255,210,120,0.24)",
  background: `
    linear-gradient(
      180deg,
      #f7cb79 0%,
      #f0b458 42%,
      #de8f2c 100%
    )
  `,
  color: "#120c04",
  textTransform: "uppercase",
  fontWeight: 800,
  fontSize: "0.72rem",
  letterSpacing: "0.16em",
  fontFamily: "'Inter', system-ui, sans-serif",
  flexDirection: "row-reverse",
  backdropFilter: "blur(10px)",
  boxShadow: `
    inset 0 1px 0 rgba(255,255,255,0.34),
    inset 0 -6px 12px rgba(120,60,0,0.18),
    0 10px 30px rgba(232,163,61,0.24),
    0 0 0 1px rgba(255,190,90,0.05)
  `,
  transition: `
    transform 260ms cubic-bezier(0.22,1,0.36,1),
    box-shadow 260ms cubic-bezier(0.22,1,0.36,1),
    filter 260ms cubic-bezier(0.22,1,0.36,1),
    border-color 260ms ease
  `,
  "& .MuiButton-startIcon": {
    marginLeft: "10px",
    marginRight: 0,
    position: "relative",
    zIndex: 2,
    transition: "transform 220ms cubic-bezier(0.22, 1, 0.36, 1)",
  },
  "& .MuiButton-startIcon svg": {
    fontSize: "1rem",
  },
  "& .MuiButton-startIcon svg path": {
    fill: "currentColor",
  },
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: "-140%",
    width: "58%",
    height: "100%",
    background: `
      linear-gradient(
        90deg,
        transparent,
        rgba(255,255,255,0.38),
        transparent
      )
    `,
    transform: "skewX(-24deg)",
    transition: "left 900ms cubic-bezier(0.22,1,0.36,1)",
    pointerEvents: "none",
  },
  "&::after": {
    content: '""',
    position: "absolute",
    inset: 0,
    borderRadius: "inherit",
    background: `
      radial-gradient(
        circle at top,
        rgba(255,255,255,0.22),
        transparent 72%
      )
    `,
    opacity: 0.7,
    pointerEvents: "none",
  },

  "&:hover": {
    transform: "translateY(-2px)",
    borderColor: "rgba(255,220,150,0.4)",
    boxShadow: `
      inset 0 1px 0 rgba(255,255,255,0.42),
      inset 0 -8px 14px rgba(120,60,0,0.22),
      0 18px 42px rgba(232,163,61,0.34),
      0 0 26px rgba(246,185,76,0.16)
    `,
    filter: "brightness(1.03)",
    "&::before": {
      left: "155%",
    },
    "& .MuiButton-startIcon": {
      transform: "translateX(3px)",
    },
  },
  "&:active": {
    transform: "scale(0.985)",
  },
  [`@media ${ULTRA_WIDE_MEDIA}`]: {
    height: 52,
    minHeight: 52,
    px: "2.5rem",
    fontSize: "0.85rem",
    borderRadius: "16px",
    "& .MuiButton-startIcon svg": {
      fontSize: "1.15rem",
    },
  },
};

export default function DarkHeroHeader({
  isAuthenticated,
  googleResponse,
  onFailure,
}) {
  const location = useLocation();
  const track = useTemplateTrack();
  const isScrolled = useHeaderScrolled();
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [loginAnchorEl, setLoginAnchorEl] = React.useState(null);
  const isLoginMenuOpen = Boolean(loginAnchorEl);

  React.useEffect(() => {
    setLoginAnchorEl(null);
  }, [location.pathname]);

  // Page canvas matches hero; global body is #3A3A3A and bleeds through a transparent fixed header.
  React.useEffect(() => {
    const { documentElement: html, body } = document;
    const prevHtmlBg = html.style.backgroundColor;
    const prevBodyBg = body.style.backgroundColor;
    html.style.backgroundColor = DARK_HERO_PAGE_BG;
    body.style.backgroundColor = DARK_HERO_PAGE_BG;
    return () => {
      html.style.backgroundColor = prevHtmlBg;
      body.style.backgroundColor = prevBodyBg;
    };
  }, []);

  React.useEffect(() => {
    const mq = window.matchMedia(NAV_INLINE_MEDIA);
    const onChange = () => {
      if (mq.matches) {
        setMobileOpen(false);
      }
    };
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const openLoginMenu = (event) => {
    track("login_menu_opened", { surface: "header" });
    setLoginAnchorEl(event.currentTarget);
  };

  return (
    <>
      <MotionBox
      component="header"
      initial={{ y: -18, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{
        duration: 0.55,
        ease: [0.22, 1, 0.36, 1],
      }}
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        width: "100%",
        zIndex: mobileOpen ? LOGIN_MENU_Z_INDEX : 1400,
        display: "flex",
        justifyContent: "center",
        backgroundColor: "transparent",
        ...OVERFLOW_X_CLIP_SX,
      }}
    >
      <MotionBox
        aria-hidden
        initial={false}
        animate={{ opacity: isScrolled ? 1 : 0 }}
        transition={HEADER_SCROLL_TRANSITION}
        sx={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          willChange: "opacity",
          ...HEADER_SCROLLED_SHELL_SX,
        }}
      />
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          top: 0,
          left: "50%",
          transform: "translateX(-50%)",
          width: "35%",
          height: "1px",
          zIndex: 1,
          pointerEvents: "none",
          background: `
            linear-gradient(
              90deg,
              transparent,
              rgba(246,185,76,0.9),
              transparent
            )
          `,
          boxShadow: "0 0 18px rgba(246,185,76,0.45)",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          opacity: 0.025,
          backgroundImage: `
            radial-gradient(
              rgba(255,255,255,0.8) 0.4px,
              transparent 0.4px
            )
          `,
          backgroundSize: "6px 6px",
          pointerEvents: "none",
        }}
      />
      <PageContainer
        sx={{
          maxWidth: HEADER_CONTENT_MAX_WIDTH,
          width: "100%",
          px: HEADER_INLINE_PADDING,
        }}
      >
        <Box sx={{ width: "100%", boxSizing: "border-box", gap: "0.4rem" }}>
          <Box
            sx={{
              position: "relative",
              zIndex: 2,
              display: "grid",
              gridTemplateColumns: "auto 1fr",
              alignItems: "center",
              columnGap: { xs: 1, xl: 3 },
              [`@media ${NAV_INLINE_MEDIA}`]: {
                gridTemplateColumns: "minmax(0, auto) minmax(0, 1fr) minmax(0, auto)",
                columnGap: "1.5rem",
              },
              transition: "all 0.3s ease",
              height: { xs: 64, xl: 72 },
              [`@media ${ULTRA_WIDE_MEDIA}`]: {
                height: 84,
                columnGap: 4,
              },
              width: "100%",
              boxSizing: "border-box",
            }}
          >
            <Box
              component={Link}
              to="/"
              sx={{
                gridColumn: 1,
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
                alignItems: "center",
                gap: "0.5rem",
                textDecoration: "none",
                flexShrink: 0,
              }}
            >
              <MotionBox
                component="img"
                alt="Ouro"
                src={LOGO_URL}
                sx={{
                  maxHeight: 34,
                  width: "auto",
                  display: "block",
                  backgroundColor: "transparent",
                  imageRendering: "auto",
                  objectFit: "contain",
                  [`@media ${ULTRA_WIDE_MEDIA}`]: {
                    height: 48,
                    maxHeight: 48,
                  },
                }}
              />
            </Box>

            <Box
              component="nav"
              aria-label="Primary"
              sx={{
                display: "none",
                gridColumn: 2,
                alignItems: "center",
                justifyContent: "center",
                minWidth: 0,
                width: "100%",
                flexWrap: "nowrap",
                [`@media ${NAV_INLINE_MEDIA}`]: {
                  display: "flex",
                  gap: "0.75rem",
                },
                [`@media (min-width: 1100px)`]: {
                  gap: "1rem",
                },
                [`@media (min-width: 1400px)`]: {
                  gap: "1.35rem",
                },
                [`@media ${ULTRA_WIDE_MEDIA}`]: {
                  gap: "1.75rem",
                },
              }}
            >
              {DARK_HERO_NAV_LINKS.map((item) => (
                <MotionButton
                  key={item.to}
                  component={Link}
                  to={item.to}
                  whileHover={{ y: -1.5 }}
                  whileTap={{ scale: 0.985 }}
                  sx={{
                    ...NAV_LINK_SX,
                    ...(item.hideBelow
                      ? { [`@media (max-width: ${item.hideBelow - 1}px)`]: { display: "none" } }
                      : {}),
                  }}
                >
                  {item.label}
                </MotionButton>
              ))}
            </Box>

            <Box
              sx={{
                gridColumn: 2,
                justifySelf: "end",
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: { xs: "0.75rem", md: "1rem" },
                [`@media ${NAV_INLINE_MEDIA}`]: {
                  gridColumn: 3,
                },
                flexShrink: 0,
              }}
            >
              {isAuthenticated ? (
                <Button
                  component={Link}
                  to="/lobby"
                  variant="contained"
                  disableElevation
                  startIcon={<ArrowForwardRounded />}
                  sx={PLAY_NOW_BUTTON_SX}
                >
                  Lobby
                </Button>
              ) : (
                <Box
                  sx={{
                    display: "none",
                    [`@media ${NAV_INLINE_MEDIA}`]: {
                      display: "block",
                    },
                  }}
                >
                  <Button sx={LOGIN_BUTTON_SX} onClick={openLoginMenu}>
                    <span>Login</span>
                  </Button>
                </Box>
              )}

              <IconButton
                onClick={() => setMobileOpen((open) => !open)}
                sx={{
                  display: "inline-flex",
                  [`@media ${NAV_INLINE_MEDIA}`]: {
                    display: "none",
                  },
                  color: "rgba(255,255,255,0.9)",
                  ml: 0.5,
                }}
                aria-label={mobileOpen ? "Close navigation" : "Open navigation"}
                aria-expanded={mobileOpen}
              >
                {mobileOpen ? <CloseIcon /> : <MenuIcon />}
              </IconButton>
            </Box>
          </Box>
        </Box>
      </PageContainer>

      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        PaperProps={{
          sx: {
            width: "100%",
            backgroundColor: "rgba(5,5,5,0.98)",
            color: "#ffffff",
            borderLeft: "1px solid rgba(255,255,255,0.06)",
            backdropFilter: "blur(24px)",
            zIndex: 1500,
          },
        }}
      >
        <Box
          sx={{
            pt: "5.25rem",
            px: 3,
            pb: 4,
            height: "100%",
            overflowY: "auto",
            boxSizing: "border-box",
          }}
        >
          <List disablePadding>
            {DARK_HERO_NAV_LINKS.map((item) => (
              <ListItem key={item.to} disablePadding>
                <ListItemButton
                  component={Link}
                  to={item.to}
                  onClick={() => setMobileOpen(false)}
                  sx={MOBILE_NAV_ITEM_SX}
                >
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontSize: "0.8rem",
                      fontWeight: 600,
                      letterSpacing: "0.14em",
                      textTransform: "uppercase",
                      fontFamily: "'Inter', system-ui, sans-serif",
                      color:
                        location.pathname === item.to
                          ? colors.primary.main
                          : "rgba(255,255,255,0.75)",
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
            {isAuthenticated ? (
              <ListItem disablePadding sx={{ mt: 2 }}>
                <ListItemButton
                  component={Link}
                  to="/lobby"
                  onClick={() => setMobileOpen(false)}
                  sx={{ ...MOBILE_NAV_ITEM_SX, maxWidth: "100%" }}
                >
                  <ListItemText
                    primary="Lobby"
                    primaryTypographyProps={{
                      fontWeight: 700,
                      letterSpacing: "0.12em",
                      textTransform: "uppercase",
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ) : null}
          </List>

          {!isAuthenticated ? (
            <Box
              sx={{
                mt: 3,
                pt: 3,
                mx: -3,
                px: 3,
                borderTop: "1px solid rgba(255,255,255,0.08)",
              }}
            >
              <Box sx={{ width: "100%", maxWidth: "100%" }}>
                <Typography sx={{ ...LOGIN_MENU_TITLE_SX, mb: 1.5, color: colors.text.primary }}>
                  Login
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                  <Box>
                    <SteamSignInButton
                      fullWidth
                      label="Sign in with Steam"
                      logoSize={22}
                      showAssurance={false}
                      surface="mobile_public_header"
                      sx={LOGIN_MENU_STEAM_SX}
                    />
                    <Box sx={LOGIN_MENU_ASSURANCE_SX}>
                      <Typography sx={LOGIN_MENU_ASSURANCE_TEXT_SX}>
                        Read-only sign-in — we never touch your inventory
                      </Typography>
                    </Box>
                  </Box>
                  <Box>
                    <PlaySafeSignInButton
                      fullWidth
                      label="Sign in with PlaySafe ID"
                      surface="mobile_public_header"
                      logoHeight={22}
                      sx={LOGIN_MENU_PLAYSAFE_SX}
                    />
                    <Box sx={LOGIN_MENU_ASSURANCE_SX}>
                      <Typography sx={LOGIN_MENU_ASSURANCE_TEXT_SX}>Play instantly</Typography>
                    </Box>
                  </Box>
                  <Box>
                    <Box
                      sx={LOGIN_MENU_GOOGLE_WRAP_SX}
                      onClick={() => track("sso_google_clicked", { provider: "google" })}
                    >
                      <GoogleLogin
                        shape="pill"
                        size="large"
                        theme="filled_black"
                        text="continue_with"
                        width={224}
                        onSuccess={googleResponse}
                        onError={onFailure}
                      />
                    </Box>
                    <Box sx={LOGIN_MENU_ASSURANCE_SX}>
                      <Typography sx={LOGIN_MENU_ASSURANCE_TEXT_SX}>
                        Play after a 3–24h check
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          ) : null}
        </Box>
      </Drawer>
      </MotionBox>

      {!isAuthenticated ? (
      <Popover
        open={isLoginMenuOpen}
        anchorEl={loginAnchorEl}
        onClose={() => setLoginAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        disableScrollLock
        sx={{ zIndex: LOGIN_MENU_Z_INDEX }}
        slotProps={{ paper: { sx: LOGIN_MENU_PAPER_SX } }}
      >
        <Typography sx={LOGIN_MENU_TITLE_SX}>
          Sign in
        </Typography>
        <Box sx={LOGIN_MENU_STACK_SX}>
          <Box>
            <SteamSignInButton
              fullWidth
              label="Sign in with Steam"
              logoSize={22}
              showAssurance={false}
              surface="public_header"
              sx={LOGIN_MENU_STEAM_SX}
            />
            <Box sx={LOGIN_MENU_ASSURANCE_SX}>
              <Typography sx={LOGIN_MENU_ASSURANCE_TEXT_SX}>
                Read-only sign-in — we never touch your inventory
              </Typography>
            </Box>
          </Box>
          <Box>
            <PlaySafeSignInButton
              fullWidth
              label="Sign in with PlaySafe ID"
              surface="public_header"
              logoHeight={22}
              sx={LOGIN_MENU_PLAYSAFE_SX}
            />
            <Box sx={LOGIN_MENU_ASSURANCE_SX}>
              <Typography sx={LOGIN_MENU_ASSURANCE_TEXT_SX}>Play instantly</Typography>
            </Box>
          </Box>
          <Box>
            <Box
              sx={LOGIN_MENU_GOOGLE_WRAP_SX}
              onClick={() => track("sso_google_clicked", { provider: "google", surface: "header" })}
            >
              <GoogleLogin
                shape="pill"
                size="large"
                theme="filled_black"
                text="continue_with"
                width={288}
                onSuccess={googleResponse}
                onError={onFailure}
              />
            </Box>
            <Box sx={LOGIN_MENU_ASSURANCE_SX}>
              <Typography sx={LOGIN_MENU_ASSURANCE_TEXT_SX}>
                Play after a 3–24h check
              </Typography>
            </Box>
          </Box>
        </Box>
      </Popover>
      ) : null}
    </>
  );
}

DarkHeroHeader.propTypes = {
  isAuthenticated: PropTypes.bool,
  googleResponse: PropTypes.func,
  onFailure: PropTypes.func,
};
